import { Building2 } from 'lucide-react';
import { DiscardChangesDialog } from '@/components/common/DiscardChangesDialog';
import {
  FormDialog,
  FormSection,
  FormStatusField,
  FormTextField,
} from '@/components/common/FormDialog';
import {
  INSTITUTE_TYPE_CODE_MAX_LENGTH,
  INSTITUTE_TYPE_NAME_MAX_LENGTH,
} from '../constants/instituteType.constants';
import { useInstituteTypeForm } from '../hooks/useInstituteTypeForm';
import type { InstituteType } from '../types/instituteType.types';

interface InstituteTypeFormModalProps {
  instituteType: InstituteType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InstituteTypeFormModal({
  instituteType,
  open,
  onOpenChange,
}: InstituteTypeFormModalProps) {
  const {
    form,
    isEditMode,
    isPending,
    submit,
    isDiscardDialogOpen,
    requestClose,
    confirmDiscard,
    cancelDiscard,
  } = useInstituteTypeForm({
    instituteType,
    open,
    onClose: () => onOpenChange(false),
  });

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      requestClose();
      return;
    }
    onOpenChange(nextOpen);
  }

  return (
    <>
      <FormDialog
        open={open}
        onOpenChange={handleOpenChange}
        icon={Building2}
        title={isEditMode ? 'Edit Institute Type' : 'Add Institute Type'}
        description="An institute type groups institutes, such as schools or colleges."
        form={form}
        isPending={isPending}
        isEditMode={isEditMode}
        onSubmit={submit}
      >
        <FormSection>
          <FormTextField
            control={form.control}
            name="name"
            label="Name"
            placeholder="Government School"
            maxLength={INSTITUTE_TYPE_NAME_MAX_LENGTH}
            isRequired
          />
          <FormTextField
            control={form.control}
            name="code"
            label="Code (optional)"
            placeholder="GOVT_SCHOOL"
            maxLength={INSTITUTE_TYPE_CODE_MAX_LENGTH}
            isUpperCase
          />
        </FormSection>
        <FormStatusField
          control={form.control}
          name="isActive"
          description="Show this type when adding an institute."
        />
        <FormStatusField
          control={form.control}
          name="requiresEiin"
          label="EIIN applicable"
          description="Institutes of this type must have an EIIN number."
        />
      </FormDialog>

      <DiscardChangesDialog
        open={isDiscardDialogOpen}
        onOpenChange={(nextOpen) => !nextOpen && cancelDiscard()}
        onDiscard={confirmDiscard}
      />
    </>
  );
}
